import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Flex } from '@chakra-ui/react';
import { ListingComparableSetFragment } from '@client/graphql/__generated__/types';
import { LngLatBounds } from 'mapbox-gl';
import { useMemo, useRef } from 'react';
import { Map, MapRef, NavigationControl, ScaleControl } from 'react-map-gl';
import { getListingPrice } from '~/apps/consumer/utils/listing.utils';
import { useDisableMapControlProps } from '~/common/hooks/useDisableMapControlProps';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { getInitialCompsBBox } from '../../../BuyerOfferAnalysis/Comps/utils';
import { NormalizedPropertyDetails } from '../../../Listings/ListingPage/utils';
import { DEFAULT_POSITION } from '../../../MapSearch/constants';
import { ExploreMapMarker } from '../../../MapSearch/ExploreMap/ExploreMapMarker';
import { CompsMiniMapMarker } from './CompsMiniMapMarker';

export const CompsMap = ({
  mainListing,
  comparableSet,
  isMini = false,
}: {
  mainListing: NormalizedPropertyDetails;
  comparableSet?: ListingComparableSetFragment;
  isMini?: boolean;
}) => {
  const isMobile = useIsMobile();
  const mapRef = useRef<MapRef>(null);
  const disableMapControlProps = useDisableMapControlProps();
  const initialBBox = getInitialCompsBBox(comparableSet, mainListing, 1);
  const initialBounds = initialBBox
    ? new LngLatBounds([
        initialBBox[0],
        initialBBox[1],
        initialBBox[2],
        initialBBox[3],
      ])
    : undefined;

  const markers = useMemo(() => {
    if (isMini) {
      const mainMarker = mainListing?.location ? (
        <CompsMiniMapMarker
          key={`marker-${mainListing.id}`}
          isMainListing={true}
          lat={mainListing.location.lat}
          lng={mainListing.location.lon}
        />
      ) : null;

      const comparableMarkers = comparableSet?.listingComparables.map(
        ({ mlsListing }) => {
          if (
            !mlsListing.geo?.location?.lat ||
            !mlsListing.geo?.location?.lon
          ) {
            return null;
          }

          return (
            <CompsMiniMapMarker
              key={`marker-${mlsListing.id}`}
              isMainListing={false}
              lat={mlsListing.geo?.location.lat}
              lng={mlsListing.geo?.location.lon}
            />
          );
        }
      );

      return [...(comparableMarkers ?? []), mainMarker];
    }

    const mainMarker = mainListing?.location ? (
      <ExploreMapMarker
        key={`marker-${mainListing.id}`}
        bgOverride="white"
        colorOverride="black"
        isPremium={true}
        lat={mainListing.location.lat}
        lng={mainListing.location.lon}
        price={mainListing.listPrice}
        zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
      />
    ) : null;

    const comparableMarkers = comparableSet?.listingComparables.map(
      ({ mlsListing }) => {
        if (!mlsListing.geo?.location?.lat || !mlsListing.geo?.location?.lon) {
          return null;
        }

        const price = getListingPrice(mlsListing);

        return (
          <ExploreMapMarker
            key={`marker-${mlsListing.id}`}
            isPremium={true}
            lat={mlsListing.geo.location.lat}
            lng={mlsListing.geo.location.lon}
            price={price}
            zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
          />
        );
      }
    );

    return [...(comparableMarkers ?? []), mainMarker];
  }, [comparableSet, mainListing, mapRef, isMini]);

  return (
    <Flex
      direction={{ base: 'column-reverse', xl: 'column' }}
      flexBasis={0}
      flexGrow={1}
      height="100%"
    >
      <Map
        ref={mapRef}
        {...disableMapControlProps}
        dragPan={!isMini}
        initialViewState={{
          bounds: initialBounds,
          longitude: mainListing?.location?.lon,
          latitude: mainListing?.location?.lat,
          zoom: DEFAULT_POSITION.zoom,
        }}
        mapStyle="mapbox://styles/mapbox/navigation-guidance-night-v4"
        mapboxAccessToken={window.ENVS.mapboxApiKey}
        minZoom={3}
        style={{
          borderRadius: isMobile && !isMini ? '0px' : '6px',
        }}
        testMode={window.ENVS.environment === 'e2e'}
      >
        {!isMini && (
          <>
            <NavigationControl />
            <ScaleControl />
          </>
        )}
        {markers}
      </Map>
    </Flex>
  );
};
